import React, { useState } from 'react';
import StoreLabel from './StoreLabel';
import { openPrintDialog, shareToLine, downloadAsImage } from '../../utils/printUtils';
import { PACKAGE_FEATURES, isFeatureEnabled } from '../../data/packageModels';
import { savePrintHistory, PRINT_TYPES, TEMPLATE_TYPES } from '../../utils/printHistoryUtils';
import { useAuth } from '../../context/AuthContext';

/**
 * Modal สำหรับพิมพ์ใบปะหน้าของร้านค้า
 */
const StoreLabelPrintModal = ({ 
  isOpen, 
  onClose, 
  store,
  packageType 
}) => {
  const { currentUser } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState('a4');
  const [savingHistory, setSavingHistory] = useState(false);

  if (!isOpen) return null;

  // ตรวจสอบว่า package รองรับ shipping label หรือไม่
  const canPrint = isFeatureEnabled(packageType, PACKAGE_FEATURES.SHIPPING_LABEL);
  
  if (!canPrint) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-4">⚠️ ไม่สามารถพิมพ์ได้</h3>
          <p className="text-gray-600 mb-4">
            ฟีเจอร์นี้สำหรับร้านที่สมัครแพ็กเกจ Basic ขึ้นไป
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
          >
            ปิด
          </button>
        </div>
      </div>
    );
  }

  const handlePrint = async () => {
    // บันทึก Print History (เฉพาะ Premium/Enterprise)
    const hasPrintHistory = isFeatureEnabled(packageType, PACKAGE_FEATURES.PRINT_HISTORY);
    if (hasPrintHistory && currentUser && store) {
      try {
        setSavingHistory(true);
        await savePrintHistory({
          storeId: store.id,
          orderId: null,
          printType: PRINT_TYPES.STORE_LABEL,
          templateType: selectedTemplate === 'a4' ? TEMPLATE_TYPES.A4 : 
                       selectedTemplate === 'sticker' ? TEMPLATE_TYPES.STICKER : 
                       TEMPLATE_TYPES.COMPACT,
          printData: {
            storeName: store.name,
            storeContact: store.contact
          },
          printedBy: currentUser.uid,
          printedAt: new Date()
        });
      } catch (error) {
        console.error('Error saving print history:', error);
      } finally {
        setSavingHistory(false);
      }
    }
    
    openPrintDialog('store-label-print');
  };

  const handleShareToLine = async () => {
    // สร้างข้อความสำหรับ LINE
    const storeName = store?.name || 'ร้านค้า';
    const phone = store?.contact?.phone || '';
    const lineId = store?.contact?.lineId || '';
    const address = store?.contact?.address || '';
    const email = store?.contact?.email || '';
    
    // สร้างข้อความที่สวยงาม
    let message = `🏪 ${storeName}\n\n`;
    if (phone) message += `📞 ${phone}\n`;
    if (lineId) message += `💬 LINE: ${lineId}\n`;
    if (email) message += `📧 ${email}\n`;
    if (address) message += `📍 ${address}\n`;
    
    // ใช้ LINE Share Button API (ถ้าเป็น mobile) หรือ URL scheme
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      // Mobile - ใช้ LINE URL scheme
      const lineUrl = `https://line.me/R/msg/text/?${encodeURIComponent(message)}`;
      window.open(lineUrl, '_blank');
    } else {
      // Desktop - ใช้ LINE Share Button
      const lineUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(message)}`;
      window.open(lineUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-lg font-semibold">🖨️ พิมพ์ใบปะหน้าของร้านค้า</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          {/* Template Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              เลือก Template
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setSelectedTemplate('a4')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                  selectedTemplate === 'a4'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-sm font-semibold">A4</div>
                <div className="text-xs text-gray-600">21x29.7cm</div>
              </button>
              <button
                onClick={() => setSelectedTemplate('sticker')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                  selectedTemplate === 'sticker'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-sm font-semibold">Sticker</div>
                <div className="text-xs text-gray-600">10x15cm</div>
              </button>
              <button
                onClick={() => setSelectedTemplate('compact')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                  selectedTemplate === 'compact'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-sm font-semibold">Compact</div>
                <div className="text-xs text-gray-600">8x12cm</div>
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
            <div className="text-sm font-semibold text-gray-700 mb-3">👁️ Preview</div>
            <div className="bg-white rounded p-4 overflow-auto" style={{ maxHeight: '400px' }}>
              <StoreLabel
                store={store}
                templateType={selectedTemplate}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={handleShareToLine}
              className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              💬 Share ไป LINE
            </button>
            <button
              onClick={handlePrint}
              disabled={savingHistory}
              className="flex-1 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingHistory ? '⏳ กำลังบันทึก...' : '🖨️ พิมพ์'}
            </button>
          </div>

          {/* Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-800">
              <strong>💡 คำแนะนำ:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>สามารถพิมพ์หรือ Share ไป LINE ได้</li>
                <li>QR Code จะเป็น LINE ID หรือเบอร์โทร (ถ้ามี)</li>
                <li>เหมาะสำหรับแปะหน้าหรือแจกให้ลูกค้า</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreLabelPrintModal;

